import React from 'react';

import { SettingsPageHeaderTitle } from 'src/legacy/components/Settings/SettingsPageHeaderTitle';
import type { RouteAccess } from 'src/routes/routeAccess';

import { AccessGate } from '../AccessGate';
import { useShell } from '../AppShell';
import { useHeaderSlot } from '../AppShell/HeaderSlotContext';

import { DefaultVariant } from './variants/DefaultVariant';
import { FullbleedVariant } from './variants/FullbleedVariant';
import { SettingsVariant } from './variants/SettingsVariant';

export type PageShellVariant = 'default' | 'fullbleed' | 'settings';

// Hoisted to module scope so the JSX element is referentially stable across
// renders, preventing the useEffect in PageShell from firing every render.
const DEFAULT_SETTINGS_HEADER = <SettingsPageHeaderTitle showMenu />;

export interface PageShellProps {
  children: React.ReactNode;
  /** Custom header content injected into AppShell's AppBar via HeaderSlotContext. */
  header?: React.ReactNode;
  /** Toolbar rendered above the page content (below the AppBar). */
  toolbar?: React.ReactNode;
  /** Layout variant. Defaults to 'default'. */
  variant?: PageShellVariant;
  /** Permission key checked by AccessGate. When omitted, no access check is performed. */
  access?: keyof RouteAccess;
  /** Allow unauthenticated access (forwarded to AccessGate). */
  unauthAccess?: boolean;
  /** Whether to apply horizontal gutters/spacing. Defaults to true. */
  showSpace?: boolean;
  /** Optional max-width constraint for the content area. */
  maxWidth?: string;
  /** When true, removes default header padding in AppShell's AppBar. */
  ignoreHeaderPadding?: boolean;
  /** Whether to show the sidebar. Defaults to true. */
  showSidebar?: boolean;
  /** When true, hides the default title/description area in the settings variant.
   *  Use for pages that render their own title (e.g. via ModuleContainer). */
  hideSettingsDescription?: boolean;
  /** When true, shows the context bar on the right side of the layout */
  showContextBar?: boolean;
}

export const PageShell: React.FC<PageShellProps> = ({
  children,
  header,
  toolbar,
  variant = 'default',
  access,
  unauthAccess,
  showSpace = true,
  maxWidth,
  ignoreHeaderPadding = false,
  showSidebar = true,
  hideSettingsDescription = false,
  showContextBar = false,
}) => {
  const { setHeader, clearHeader, setHeaderPadding, setShowSidebar } =
    useHeaderSlot();
  const { setShowContextBar } = useShell();

  // For fullbleed, force header to null to hide AppBar content.
  // For settings, default header to DEFAULT_SETTINGS_HEADER (mobile-only back toggle).
  const resolvedHeader =
    variant === 'fullbleed'
      ? null
      : variant === 'settings'
        ? (header ?? DEFAULT_SETTINGS_HEADER)
        : header;

  // Set the header slot on mount / when header prop changes, clear on unmount.
  React.useEffect(() => {
    if (resolvedHeader !== undefined) {
      setHeader(resolvedHeader);
    }

    setHeaderPadding(ignoreHeaderPadding);
    setShowSidebar(showSidebar);
    setShowContextBar(showContextBar);
    return () => {
      clearHeader();
      setHeaderPadding(false);
      setShowSidebar(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedHeader, ignoreHeaderPadding, showSidebar]);

  const renderVariant = () => {
    switch (variant) {
      case 'fullbleed':
        return <FullbleedVariant>{children}</FullbleedVariant>;
      case 'settings':
        return (
          <SettingsVariant hideDescription={hideSettingsDescription}>
            {children}
          </SettingsVariant>
        );
      case 'default':
      default:
        return (
          <DefaultVariant showSpace={showSpace} maxWidth={maxWidth}>
            {children}
          </DefaultVariant>
        );
    }
  };

  const content = (
    <>
      {toolbar}
      {renderVariant()}
    </>
  );

  if (access !== undefined) {
    return (
      <AccessGate access={access} unauthAccess={unauthAccess}>
        {content}
      </AccessGate>
    );
  }

  return content;
};
